(() => {
  if (customElements.get('mas-help-page')) return;
  const normalize = (value) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  class MasHelpPage extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.groups = [...this.querySelectorAll('[data-help-source] .mas-faq__section')];
      if (!this.groups.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.input = this.querySelector('[data-help-input]');
      this.heading = this.querySelector('[data-help-title]');
      this.status = this.querySelector('[data-help-status]');
      this.empty = this.querySelector('[data-help-empty]');
      this.clear = this.querySelector('[data-help-clear]');
      this.topics = [...this.querySelectorAll('[data-help-topic]')];
      this.entries = this.groups.flatMap((group) => [...group.querySelectorAll('details')].map((details, index) => {
        details.id ||= `faq-${group.id}-${index + 1}`;
        return { details, group, text: normalize(details.textContent), initialOpen: details.open };
      }));
      this.selected = this.groups[0].id;
      this.topics.forEach((topic) => {
        const count = this.entries.filter((entry) => entry.group.id === topic.dataset.helpTopic).length;
        topic.querySelector('[data-help-count]').textContent = `${count} question${count === 1 ? '' : 's'}`;
        topic.addEventListener('click', (event) => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          this.select(topic.dataset.helpTopic);
          history.replaceState(null, '', `#${this.selected}`);
        }, options);
      });
      this.input.addEventListener('input', () => this.render(), options);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.input.value) {
          event.preventDefault();
          this.resetSearch();
        }
      }, options);
      this.querySelector('[data-help-search]').addEventListener('submit', (event) => event.preventDefault(), options);
      this.clear.addEventListener('click', () => this.resetSearch(), options);
      this.querySelector('[data-help-reset]').addEventListener('click', () => this.resetSearch(), options);
      this.querySelectorAll('[data-help-question]').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          const entry = this.entries.find(({ details }) => details.querySelector('summary').textContent.trim() === link.dataset.helpQuestion);
          if (!entry) return;
          event.preventDefault();
          this.select(entry.group.id);
          this.openEntry(entry);
          history.replaceState(null, '', `#${entry.details.id}`);
        }, options);
      });
      window.addEventListener('hashchange', () => this.readHash(), options);
      this.setAttribute('data-enhanced', '');
      this.querySelector('[data-help-search]').hidden = false;
      this.querySelector('[data-help-heading]').hidden = false;
      this.render();
      this.readHash();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
    select(id) {
      if (!this.groups.some((group) => group.id === id)) return;
      this.selected = id;
      this.input.value = '';
      this.render();
    }
    resetSearch() {
      this.input.value = '';
      this.render();
      this.input.focus({ preventScroll: true });
    }
    readHash() {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      const entry = this.entries.find(({ details }) => details.id === id);
      if (entry) {
        this.select(entry.group.id);
        this.openEntry(entry);
      } else if (this.groups.some((group) => group.id === id)) {
        this.select(id);
        this.groups.find((group) => group.id === id).scrollIntoView({ block: 'start', behavior: 'instant' });
      }
    }
    openEntry(entry) {
      entry.details.open = true;
      entry.details.querySelector('summary').focus({ preventScroll: true });
      entry.details.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
    render() {
      const query = this.input.value.trim();
      const words = normalize(query).split(' ').filter(Boolean);
      const searching = query.length > 0;
      let count = 0;
      this.toggleAttribute('data-searching', searching);
      this.entries.forEach((entry) => {
        const visible = searching ? words.length > 0 && words.every((word) => entry.text.includes(word)) : entry.group.id === this.selected;
        entry.details.hidden = !visible;
        if (visible) count += 1;
        if (this.wasSearching !== searching) entry.details.open = searching ? false : entry.initialOpen;
      });
      this.groups.forEach((group) => {
        group.hidden = !this.entries.some((entry) => entry.group === group && !entry.details.hidden);
      });
      this.topics.forEach((topic) => {
        if (!searching && topic.dataset.helpTopic === this.selected) topic.setAttribute('aria-current', 'true');
        else topic.removeAttribute('aria-current');
      });
      const selectedTopic = this.topics.find((topic) => topic.dataset.helpTopic === this.selected);
      this.heading.textContent = searching ? 'Search results' : selectedTopic?.querySelector('strong').textContent || 'Questions & answers';
      this.status.textContent = searching ? `${count} ${count === 1 ? 'answer' : 'answers'} for “${query}”` : `${count} ${count === 1 ? 'question' : 'questions'}`;
      this.clear.hidden = !query;
      this.empty.hidden = count > 0;
      this.wasSearching = searching;
    }
  }
  customElements.define('mas-help-page', MasHelpPage);
})();
